import { useState, useEffect, useCallback, useMemo } from 'react';
import { DEFAULT_ROWS_PER_PAGE } from '../constants';

/**
 * Hook pour gérer la pagination de manière sécurisée
 */
export const usePagination = (totalItems, rowsPerPage = DEFAULT_ROWS_PER_PAGE) => {
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(rowsPerPage);

  const metrics = useMemo(() => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const isValidPage = page >= 0 && page < totalPages;
    
    return {
      totalItems,
      totalPages: Math.max(1, totalPages),
      currentPage: page,
      isValidPage: totalItems === 0 || isValidPage,
      hasNext: page + 1 < totalPages,
      hasPrev: page > 0
    };
  }, [totalItems, itemsPerPage, page]);

  // Réinitialiser la page si elle devient invalide
  useEffect(() => {
    if (!metrics.isValidPage && metrics.totalItems > 0) {
      setPage(0);
    }
  }, [metrics]);

  const safeSetPage = useCallback((newPageOrFn) => {
    setPage(currentPage => {
      const nextPage = typeof newPageOrFn === 'function' ? newPageOrFn(currentPage) : newPageOrFn;
      const maxPage = metrics ? Math.max(0, metrics.totalPages - 1) : 0;
      return Math.max(0, Math.min(maxPage, nextPage));
    });
  }, [metrics]);

  const safeSetItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setPage(0);
  }, []);

  const resetPage = useCallback(() => {
    setPage(0);
  }, []);

  const paginateData = useCallback((data) => {
    if (data.length <= itemsPerPage) return data;
    return data.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  }, [page, itemsPerPage]);

  return {
    page,
    itemsPerPage,
    metrics,
    safeSetPage,
    safeSetItemsPerPage,
    resetPage,
    paginateData
  };
};
